/**
 * screens/hooks/useMonitor.ts
 * Live data for the read-only POOL monitors (SK Voice / SK Chat). Polls the
 * monitor read model (no WS in queue-api) and exposes it; pass null to disable
 * (memory mode / no active queue). The live read model is intentionally thinner
 * than the in-memory demo (privacy: tokens not names; agents are pool units with
 * load/capacity, not named people) — the screens render whatever the source
 * provides and the live branch maps to that thinner shape.
 */

import { useEffect, useState, useCallback } from 'react';
import { getVoiceMonitor, getChatMonitor } from '../../services/queue/queueService';
import { handleApiError } from '../../utils/errorHandler';
import { useQueueStream } from './useQueueStream';
import type { VoiceMonitorView, ChatMonitorView } from '../../api/queueDtos';

// SSE-pushed; poll is the fallback (short when the stream is down, slow when up).
const POLL_MS = 4000;
const POLL_MS_STREAMING = 30000;

export function useVoiceMonitor(queueId: string | null): { view: VoiceMonitorView | null } {
  const [view, setView] = useState<VoiceMonitorView | null>(null);
  const fetchOnce = useCallback((id: string) => {
    getVoiceMonitor(id).then(setView).catch((err) => handleApiError(err, 'Could not load voice monitor'));
  }, []);
  const { connected } = useQueueStream(queueId, () => { if (queueId) fetchOnce(queueId); });
  useEffect(() => {
    if (!queueId) { setView(null); return; }
    fetchOnce(queueId);
    const t = setInterval(() => fetchOnce(queueId), connected ? POLL_MS_STREAMING : POLL_MS);
    return () => clearInterval(t);
  }, [queueId, fetchOnce, connected]);
  return { view };
}

export function useChatMonitor(queueId: string | null): { view: ChatMonitorView | null } {
  const [view, setView] = useState<ChatMonitorView | null>(null);
  const fetchOnce = useCallback((id: string) => {
    getChatMonitor(id).then(setView).catch((err) => handleApiError(err, 'Could not load chat monitor'));
  }, []);
  const { connected } = useQueueStream(queueId, () => { if (queueId) fetchOnce(queueId); });
  useEffect(() => {
    if (!queueId) { setView(null); return; }
    fetchOnce(queueId);
    const t = setInterval(() => fetchOnce(queueId), connected ? POLL_MS_STREAMING : POLL_MS);
    return () => clearInterval(t);
  }, [queueId, fetchOnce, connected]);
  return { view };
}
